import isEqual from 'react-fast-compare';

import {assert, assertAndContinue} from 'Asserts';
import {Side} from 'constants/MolbioConstants';
import {SequenceTopology} from 'constants/SequenceConstants';
import {
  BasicPolymerInfo,
  Endpoints,
  FragmentProductionMethod,
  NormalizedFragment,
  OPPOSITE_BA_ORIENTATION,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import BulkAssemblyPolymerData from 'molbio/bulk-assembly/BulkAssemblyPolymerData';
import {
  BA_EMPTY_VALUE,
  baNumber,
  BANumberValue,
  baNumericId,
  BANumericIdValue,
  baOrientation,
  BAOrientationValue,
  BARawTextValue,
  BulkAssemblyOrientation,
  isBAId,
  isBANumber,
  isBANumericId,
  isBARawText,
  isEmptyBAValue,
} from 'molbio/bulk-assembly/BulkAssemblyValues';
import {polymerDataFromFragment} from 'molbio/bulk-assembly/util/BulkAssemblyUtil';
import {
  areEnzymesRequired,
  getRestrictionEnzymeInfo,
  RestrictionEnzymeInfo,
  RequiredEnzymesDetails,
} from 'molbio/bulk-assembly/util/EnzymeUtil';
import {NO_ENZYME_SENTINEL} from 'molbio/enzymes/EnzymeConstants';
import {getEnzyme} from 'molbio/enzymes/EnzymeUtil';
import {displayStartToModel, distance, length, modelEndToDisplay, modelToDisplay} from 'molbio/SeqUtil';
import sortedBy from 'util/sortedBy';

/**
 * Get helper functions to convert from a display-formatted index to model-formatted index.
 */
export function getDisplayToModelConverters(startIndex: number): {
  start: (value: number) => number;
  end: (value: number) => number;
} {
  return {
    start: (value) => displayStartToModel(value, startIndex),
    // Existing SeqUtil helpers don't cover this case: we want the exclusive end of the selection,
    // but without any special logic of "end" as 0.
    end: (value) => value - startIndex + 1,
  };
}

/**
 * Get helper functions to convert from a model-formatted index to display-formatted index.
 */
export function getModelToDisplayConverters(startIndex: number): {
  start: (value: number) => number;
  end: (value: number) => number;
} {
  return {
    start: (value) => modelToDisplay(value, startIndex),
    end: (value) => value + startIndex - 1,
  };
}

/**
 * Get one value when the orientation is forward or the other value when orientation is reverse.
 *
 * Util to pick a value based on the orientation, mostly helps avoiding ugly ternary syntax.
 */
export function getValueForOrientation<T>(
  orientation: BulkAssemblyOrientation,
  forwardValue: T,
  reverseValue: T
): T {
  return orientation === BulkAssemblyOrientation.FORWARD ? forwardValue : reverseValue;
}

export function getInitialEnzymeCutsiteEndpoints(
  bases: string,
  circular: boolean,
  restrictionEnzymeId5: number | null,
  restrictionEnzymeId3: number | null
): Endpoints {
  if (restrictionEnzymeId5 === restrictionEnzymeId3 && restrictionEnzymeId5 != null) {
    const sortedCutsitePositions = getRestrictionEnzymeInfo(
      restrictionEnzymeId5,
      bases,
      circular
    ).cutsiteIndices!;

    return {
      start: sortedCutsitePositions.length > 0 ? baNumber(sortedCutsitePositions[0]) : BA_EMPTY_VALUE,
      end: sortedCutsitePositions.length > 1 ? baNumber(sortedCutsitePositions[1]) : BA_EMPTY_VALUE,
    };
  }
  const sortedCutsitePositions5 =
    getRestrictionEnzymeInfo(restrictionEnzymeId5, bases, circular).cutsiteIndices || [];
  const sortedCutsitePositions3 =
    getRestrictionEnzymeInfo(restrictionEnzymeId3, bases, circular).cutsiteIndices || [];

  return {
    start:
      restrictionEnzymeId5 == null || restrictionEnzymeId5 === NO_ENZYME_SENTINEL
        ? baNumber(0)
        : sortedCutsitePositions5.length > 0
          ? baNumber(sortedCutsitePositions5[0])
          : BA_EMPTY_VALUE,
    end:
      restrictionEnzymeId3 == null || restrictionEnzymeId3 === NO_ENZYME_SENTINEL
        ? baNumber(bases.length)
        : sortedCutsitePositions3.length > 0
          ? baNumber(sortedCutsitePositions3[0])
          : BA_EMPTY_VALUE,
  };
}

interface FragmentDetails extends Endpoints {
  proteinVersionDbId?: number;
  sequenceVersionDbId?: number;
  orientation: BAOrientationValue | BARawTextValue;
  restrictionEnzyme5: BANumericIdValue | BARawTextValue;
  restrictionEnzyme3: BANumericIdValue | BARawTextValue;
}

/**
 * Get autopopulated fragment details if the fragment's endpoints are editable.
 *
 * Fragment endpoints (including restriction enzymes) are locked until polymer data is resolved (once the
 * fragment is resolved to an entity, and some metadata is fetched). This function encapsulates the logic to
 * get the initial start/end values, orientation, 5'/3' restriction enzymes, and DB version used when those
 * columns become editable.
 *
 * This function also handles autopopulation in the situation that something about the fragment has changed
 * (e.g. fragment production method) that would make its current endpoint details incorrect.
 *
 * Note: Because this function is called on every render via `AssemblyCoordnator.performEffectsAfterRender`,
 * it's important that it returns null in the case of a no-op, otherwise it will cause an infinite render
 * loop.
 */
export function getAutopopulatedFragmentDetails(
  fragment: NormalizedFragment,
  polymerData: BulkAssemblyPolymerData,
  canSpecifyEndpoints: boolean,
  preferredRestrictionEnzymeId: number | null,
  fragmentBinIndex: number | null,
  numFragments: number,
  constructTopology: SequenceTopology,
  fragmentProductionMethod: FragmentProductionMethod
): null | FragmentDetails {
  const {polymer, polymerKind, polymerVersionDbId} = polymerDataFromFragment(fragment);
  // Skip underspecified fragments.
  if (!isBAId(polymer)) {
    return null;
  }
  const latestVersionDbId =
    polymerData.getLatestVersionDbId(polymer.id) ||
    fragment.sequenceVersionDbId ||
    fragment.proteinVersionDbId;
  if (latestVersionDbId == null) {
    return null;
  }
  const basicPolymerInfo = polymerData.getBasicPolymerInfo(polymer.id, latestVersionDbId);
  if (basicPolymerInfo == null) {
    return null;
  }
  const maybeAutopopulatedRestrictionEnzymes = getAutopopulatedRestrictionEnzymes(
    basicPolymerInfo,
    preferredRestrictionEnzymeId,
    fragmentBinIndex,
    constructTopology,
    numFragments,
    fragmentProductionMethod
  );
  const versionData: {
    proteinVersionDbId?: number;
    sequenceVersionDbId?: number;
  } = {};
  if (polymerKind === 'sequence') {
    versionData.sequenceVersionDbId = latestVersionDbId;
  } else {
    versionData.proteinVersionDbId = latestVersionDbId;
  }
  let maybeAutopopulatedFragmentEndpoints = null;
  let restrictionEnzyme5: BARawTextValue | BANumericIdValue | null = null;
  let restrictionEnzyme3: BARawTextValue | BANumericIdValue | null = null;
  if (maybeAutopopulatedRestrictionEnzymes != null) {
    restrictionEnzyme5 = maybeAutopopulatedRestrictionEnzymes.restrictionEnzyme5;
    restrictionEnzyme3 = maybeAutopopulatedRestrictionEnzymes.restrictionEnzyme3;
  } else {
    return null;
  }
  if (restrictionEnzyme5 != null && restrictionEnzyme3 != null && polymerVersionDbId == null) {
    maybeAutopopulatedFragmentEndpoints = getAutopopulatedFragmentEndpoints(
      basicPolymerInfo,
      canSpecifyEndpoints,
      restrictionEnzyme5,
      restrictionEnzyme3,
      {start: fragment.start, end: fragment.end}
    );
  }
  let autopopulatedFragmentEndpoints = {start: fragment.start, end: fragment.end};
  if (maybeAutopopulatedFragmentEndpoints != null) {
    autopopulatedFragmentEndpoints = {...maybeAutopopulatedFragmentEndpoints};
  }
  let autopopulatedRestrictionEnzymes = {
    restrictionEnzyme5: fragment.restrictionEnzyme5,
    restrictionEnzyme3: fragment.restrictionEnzyme3,
  };
  if (
    maybeAutopopulatedRestrictionEnzymes != null &&
    // Do not override existing restriction enzymes
    !isBANumericId(fragment.restrictionEnzyme5) &&
    !isBANumericId(fragment.restrictionEnzyme3)
  ) {
    autopopulatedRestrictionEnzymes = maybeAutopopulatedRestrictionEnzymes;
  }
  if (
    isEqual(autopopulatedFragmentEndpoints.start, fragment.start) &&
    isEqual(autopopulatedFragmentEndpoints.end, fragment.end) &&
    isEqual(autopopulatedRestrictionEnzymes.restrictionEnzyme5, fragment.restrictionEnzyme5) &&
    isEqual(autopopulatedRestrictionEnzymes.restrictionEnzyme3, fragment.restrictionEnzyme3) &&
    ((versionData.proteinVersionDbId == null && fragment.proteinVersionDbId == null) ||
      isEqual(versionData.proteinVersionDbId, fragment.proteinVersionDbId)) &&
    ((versionData.sequenceVersionDbId == null && fragment.sequenceVersionDbId == null) ||
      isEqual(versionData.sequenceVersionDbId, fragment.sequenceVersionDbId))
  ) {
    // If this is a no-op, return null.
    return null;
  }
  return {
    ...autopopulatedFragmentEndpoints,
    ...autopopulatedRestrictionEnzymes,
    ...versionData,
    orientation:
      maybeAutopopulatedFragmentEndpoints != null
        ? baOrientation(BulkAssemblyOrientation.FORWARD)
        : fragment.orientation,
  };
}

export function getAutopopulatedFragmentEndpoints(
  basicPolymerInfo: BasicPolymerInfo,
  canSpecifyEndpoints: boolean,
  restrictionEnzyme5: BARawTextValue | BANumericIdValue,
  restrictionEnzyme3: BARawTextValue | BANumericIdValue,
  existingEndpoints: Endpoints
): Endpoints | null {
  if (!isEmptyBAValue(existingEndpoints.start) || !isEmptyBAValue(existingEndpoints.end)) {
    return null;
  }
  if (
    (isBANumericId(restrictionEnzyme5) && restrictionEnzyme5.id !== NO_ENZYME_SENTINEL) ||
    (isBANumericId(restrictionEnzyme3) && restrictionEnzyme3.id !== NO_ENZYME_SENTINEL)
  ) {
    assert(
      canSpecifyEndpoints,
      'Expected restriction enzyme details to be empty if specifying endpoints is disallowed.'
    );
    return getInitialEnzymeCutsiteEndpoints(
      basicPolymerInfo.residues,
      basicPolymerInfo.circular,
      isBANumericId(restrictionEnzyme5) ? restrictionEnzyme5.id : null,
      isBANumericId(restrictionEnzyme3) ? restrictionEnzyme3.id : null
    );
  } else if (canSpecifyEndpoints) {
    return {
      start: baNumber(0),
      end:
        basicPolymerInfo.residues.length === 0 ? BA_EMPTY_VALUE : baNumber(basicPolymerInfo.residues.length),
    };
  } else {
    return {
      start: BA_EMPTY_VALUE,
      end: BA_EMPTY_VALUE,
    };
  }
}

/**
 * Auto-populate restriction enzymes for each end of the fragment.
 *
 * @param basicPolymerInfo Basic information about the polymer including its residues and circularity
 * @param preferredRestrictionEnzymeId: ID of the preferred enzyme, as in Golden Gate assembly
 * @param binIndex Index of the bin in which the fragment will be used (relative to other fragment bins)
 * @param constructTopology Topology of the output construct
 * @param numBins Number of bins in the construct
 * @param fragmentProductionMethod Fragment production method for the bin
 */
export function getAutopopulatedRestrictionEnzymes(
  basicPolymerInfo: BasicPolymerInfo,
  preferredRestrictionEnzymeId: number | null,
  binIndex: number | null,
  constructTopology: SequenceTopology,
  numBins: number,
  fragmentProductionMethod: FragmentProductionMethod
): {
  restrictionEnzyme5: BARawTextValue | BANumericIdValue;
  restrictionEnzyme3: BARawTextValue | BANumericIdValue;
} | null {
  if (preferredRestrictionEnzymeId == null) {
    return {
      restrictionEnzyme5: baNumericId(NO_ENZYME_SENTINEL),
      restrictionEnzyme3: baNumericId(NO_ENZYME_SENTINEL),
    };
  }
  const restrictionEnzymeInfo = getRestrictionEnzymeInfo(
    preferredRestrictionEnzymeId,
    basicPolymerInfo.residues,
    basicPolymerInfo.circular
  );
  const areRestrictionEnzymesRequired = areEnzymesRequired(
    binIndex,
    restrictionEnzymeInfo,
    constructTopology,
    numBins,
    fragmentProductionMethod
  );
  if (areRestrictionEnzymesRequired == null) {
    return null;
  }
  const {isStartEnzymeRequired, isEndEnzymeRequired} = areRestrictionEnzymesRequired;
  return {
    restrictionEnzyme5: isStartEnzymeRequired
      ? baNumericId(preferredRestrictionEnzymeId)
      : baNumericId(NO_ENZYME_SENTINEL),
    restrictionEnzyme3: isEndEnzymeRequired
      ? baNumericId(preferredRestrictionEnzymeId)
      : baNumericId(NO_ENZYME_SENTINEL),
  };
}

export function handleEndpointChange(
  {start, end}: Endpoints,
  orientation: BulkAssemblyOrientation,
  circular: boolean,
  singleStranded: boolean
): Endpoints & {
  orientation?: BulkAssemblyOrientation;
} {
  if (!circular && !singleStranded && isBANumber(start) && isBANumber(end) && start.value >= end.value) {
    return {
      start: end,
      end: start,
      orientation: OPPOSITE_BA_ORIENTATION[orientation],
    };
  } else {
    return {start, end};
  }
}

/**
 * Given fragment info, generate new endpoints that can be "snapped" to line up with the provided enzyme cut sites.
 *
 * This helper is only applicable when the fragment production method involves a restriction enzyme and when
 * the current selection is invalid - it allows us to provide a reasonable selection that is valid.
 *
 * Some cases handled have some nuance. For instance, if the current selection has a start endpoint on the
 * very last cut site and an undefined end endpoint, then for circular fragments we can set the end to the very
 * first cut site (crossing the origin), but if the fragment is linear then we instead suggest moving
 * the start endpoint to the second-to-last cut site and the end endpoint to the last cut site.
 */
export function getSnappableCutsiteEndpoints(
  cutsites5: Array<number> | null,
  cutsites3: Array<number> | null,
  endpoints: Endpoints,
  sequenceLength: number,
  circular: boolean
): Endpoints | null {
  // If there are different restriction enzymes used for the 5' and 3' ends, do not attempt to snap endpoints.
  if (
    cutsites5 == null ||
    cutsites3 == null ||
    !isEqual(sortedBy(cutsites5, [(cutsite) => cutsite]), sortedBy(cutsites3, [(cutsite) => cutsite]))
  ) {
    return null;
  }
  const cutsites = cutsites5;
  if (
    cutsites == null ||
    cutsites.length < 2 ||
    Object.values(endpoints).every(
      (endpoint: BANumberValue | BARawTextValue) => isBANumber(endpoint) && cutsites.includes(endpoint.value)
    )
  ) {
    // Noop if this is not an enzyme-based fragment production method, or there are insufficient cut sites,
    // or if the endpoints are already at valid cut sites.
    return null;
  }
  const closestCutsite = (position: number): number =>
    sortedBy(cutsites, [(cutsite) => distance(cutsite, position, sequenceLength, circular)])[0];

  if (isBARawText(endpoints.start) && isBARawText(endpoints.end)) {
    // If neither endpoint is a number, just use the first two cut sites.
    return {
      start: baNumber(cutsites[0]),
      end: baNumber(cutsites[1]),
    };
  }

  if (isBARawText(endpoints.start) && isBANumber(endpoints.end)) {
    // Try to define a start cut site right before the end cut site.
    const endCutsite = closestCutsite(endpoints.end.value);
    const endCutsiteIndex = cutsites.indexOf(endCutsite);
    if (endCutsiteIndex === 0 && circular) {
      return {
        start: baNumber(cutsites[cutsites.length - 1]),
        end: baNumber(endCutsite),
      };
    }
    if (endCutsiteIndex === 0 && !circular) {
      return {
        start: baNumber(cutsites[0]),
        end: baNumber(cutsites[1]),
      };
    }
    return {
      start: baNumber(cutsites[endCutsiteIndex - 1]),
      end: baNumber(endCutsite),
    };
  }

  if (
    isBANumber(endpoints.start) &&
    isBANumber(endpoints.end) &&
    closestCutsite(endpoints.start.value) !== closestCutsite(endpoints.end.value)
  ) {
    // Both endpoints are defined and their closest cut site is different, so no special logic needed.
    return {
      start: baNumber(closestCutsite(endpoints.start.value)),
      end: baNumber(closestCutsite(endpoints.end.value)),
    };
  }

  // Either just start is a number or both endpoints snap to the same cut site.
  // TS does not understand that start is definitely a number even though it's guaranteed based on prior conditionals.
  const startCutsite = closestCutsite((endpoints.start as BANumberValue).value);
  const startCutsiteIndex = cutsites.indexOf(startCutsite);
  const startCutsiteIsLastCutsite = startCutsiteIndex === cutsites.length - 1;
  if (!startCutsiteIsLastCutsite) {
    return {
      start: baNumber(startCutsite),
      end: baNumber(cutsites[startCutsiteIndex + 1]),
    };
  }
  if (circular) {
    // Circular so it's ok to wrap.
    return {
      start: baNumber(startCutsite),
      end: baNumber(cutsites[0]),
    };
  }
  // Linear so we cannot wrap - push the start back to the previous cut site.
  return {
    start: baNumber(cutsites[startCutsiteIndex - 1]),
    end: baNumber(startCutsite),
  };
}

/*
 * When indices are used to specify a range in bulk assembly, start must always be less than end. Therefore,
 * when we receive end=0, it should be coerced to the length of the input sequence.
 */
export function getEndpointAwareCutsiteIndices(
  cutsiteIndices: Array<number>,
  inputSequenceLength: number,
  side: Side
): Array<number> {
  return sortedBy(
    cutsiteIndices.map((index) => (side === 'end' && index === 0 ? inputSequenceLength : index)),
    [(cutsite) => cutsite]
  );
}

/*
 * Check whether selection endpoints are valid and generate messages if they are not.
 *
 * There are two "levels" of validation: checking whether we can present the selection on
 * the sequence map and broadly checking whether the selection can be used as a subsequence
 * of the fragment for the purposes of bulk assembly.
 *
 * Error messages are represented as strings and correspond to where the error should be presented.
 * startErrorMessage for instance will always reference "start" in the error message even though it
 * will actually be regarding the end endpoint in reverse orientation cases.
 */
export function validateEndpoints(
  {start, end}: Endpoints,
  polymerLength: number,
  startIndex: number,
  orientation: BulkAssemblyOrientation,
  restrictionEnzymeInfo5: RestrictionEnzymeInfo,
  restrictionEnzymeInfo3: RestrictionEnzymeInfo,
  canSpecifyEndpoints: boolean,
  isFragmentCircular: boolean,
  areRestrictionEnzymesRequired: RequiredEnzymesDetails
): {
  canMirrorToMap: boolean;
  length: number | null;
  startErrorMessage?: string;
  endErrorMessage?: string;
} {
  const formattedMax = modelEndToDisplay(polymerLength, polymerLength, startIndex);
  const formattedMin = modelToDisplay(0, startIndex);

  const startLabel = getValueForOrientation(orientation, 'Start', 'End');
  const endLabel = getValueForOrientation(orientation, 'End', 'Start');

  const startRestrictionEnzymeInfo = getValueForOrientation(
    orientation,
    restrictionEnzymeInfo5,
    restrictionEnzymeInfo3
  );
  const endRestrictionEnzymeInfo = getValueForOrientation(
    orientation,
    restrictionEnzymeInfo3,
    restrictionEnzymeInfo5
  );

  let canMirrorToMap = true;
  let canCalculateLength = true;
  let startErrorMessage;
  let endErrorMessage;

  if (!canSpecifyEndpoints) {
    if (!isEmptyBAValue(start)) {
      startErrorMessage = `${startLabel} must not have a value.`;
    }
    if (!isEmptyBAValue(end)) {
      endErrorMessage = `${endLabel} must not have a value.`;
    }
    assertAndContinue(
      !(startErrorMessage ?? endErrorMessage),
      'Start and end should be empty if cloning method does not support specifying endpoints'
    );
    return {
      canMirrorToMap: false,
      length: polymerLength,
      startErrorMessage: getValueForOrientation(orientation, startErrorMessage, endErrorMessage),
      endErrorMessage: getValueForOrientation(orientation, endErrorMessage, startErrorMessage),
    };
  }

  if (
    areRestrictionEnzymesRequired?.isStartEnzymeRequired &&
    (restrictionEnzymeInfo5.restrictionEnzymeId == null ||
      restrictionEnzymeInfo5.restrictionEnzymeId === NO_ENZYME_SENTINEL)
  ) {
    startErrorMessage = "5' enzyme must be specified.";
  }
  if (
    areRestrictionEnzymesRequired?.isEndEnzymeRequired &&
    (restrictionEnzymeInfo3.restrictionEnzymeId == null ||
      restrictionEnzymeInfo3.restrictionEnzymeId === NO_ENZYME_SENTINEL)
  ) {
    endErrorMessage = "3' enzyme must be specified.";
  }
  if (!isBANumber(start) && isEmptyBAValue(start)) {
    startErrorMessage = `${startLabel} must have a value.`;
    canMirrorToMap = false;
    canCalculateLength = false;
  } else if (!isBANumber(start)) {
    startErrorMessage = `${startLabel} value, ${start.rawText}, cannot be parsed.`;
    canMirrorToMap = false;
    canCalculateLength = false;
  } else if (start.value > polymerLength) {
    startErrorMessage = `${startLabel} cannot be greater than ${formattedMax}`;
    canMirrorToMap = false;
    canCalculateLength = false;
  } else if (start.value < 0) {
    startErrorMessage = `${startLabel} cannot be less than ${formattedMin}`;
    canMirrorToMap = false;
    canCalculateLength = false;
  } else if (
    startRestrictionEnzymeInfo.cutsiteIndices != null &&
    startRestrictionEnzymeInfo.restrictionEnzymeId != null &&
    startRestrictionEnzymeInfo.restrictionEnzymeId !== NO_ENZYME_SENTINEL &&
    !getEndpointAwareCutsiteIndices(
      startRestrictionEnzymeInfo.cutsiteIndices,
      polymerLength,
      'start'
    ).includes(start.value)
  ) {
    const enzymeName = getEnzyme(startRestrictionEnzymeInfo.restrictionEnzymeId).name;
    startErrorMessage = `Start must be at a cut site of ${enzymeName}`;
  }

  if (isBANumber(end) && end.value > polymerLength) {
    endErrorMessage = `${endLabel} cannot be greater than ${formattedMax}`;
    canMirrorToMap = false;
    canCalculateLength = false;
  } else if (isBANumber(end) && end.value < 1) {
    endErrorMessage = `${endLabel} cannot be less than ${formattedMin}`;
    canMirrorToMap = false;
    canCalculateLength = false;
  } else if (!isFragmentCircular && isBANumber(start) && isBANumber(end) && start.value >= end.value) {
    startErrorMessage = `${startLabel} cannot be greater than ${endLabel.toLowerCase()}`;
    endErrorMessage = `${endLabel} cannot be less than ${startLabel.toLowerCase()}`;
    canMirrorToMap = false;
    canCalculateLength = false;
  } else if (!isBANumber(end) && isEmptyBAValue(end)) {
    endErrorMessage = `${endLabel} must have a value.`;
    canCalculateLength = false;
  } else if (!isBANumber(end)) {
    endErrorMessage = `${endLabel} value, ${end.rawText}, cannot be parsed.`;
    canCalculateLength = false;
  } else if (
    endRestrictionEnzymeInfo.cutsiteIndices != null &&
    endRestrictionEnzymeInfo.restrictionEnzymeId != null &&
    endRestrictionEnzymeInfo.restrictionEnzymeId !== NO_ENZYME_SENTINEL &&
    !getEndpointAwareCutsiteIndices(endRestrictionEnzymeInfo.cutsiteIndices, polymerLength, 'end').includes(
      end.value
    )
  ) {
    const enzymeName = getEnzyme(endRestrictionEnzymeInfo.restrictionEnzymeId).name;
    endErrorMessage = `End must be at a cut site of ${enzymeName}`;
  }

  return {
    canMirrorToMap,
    length: canCalculateLength
      ? length((start as BANumberValue).value, (end as BANumberValue).value, polymerLength)
      : null,
    startErrorMessage: getValueForOrientation(orientation, startErrorMessage, endErrorMessage),
    endErrorMessage: getValueForOrientation(orientation, endErrorMessage, startErrorMessage),
  };
}

export function getBasicFragmentSelectionDetails(
  endpoints: Endpoints,
  orientation: BulkAssemblyOrientation,
  basicPolymerInfo: BasicPolymerInfo,
  canSpecifyEndpoints: boolean
): {length: number; visualStart: number; visualEnd: number | null} {
  if (!canSpecifyEndpoints) {
    return {
      length: basicPolymerInfo.residues.length,
      visualStart: 0,
      visualEnd: basicPolymerInfo.residues.length,
    };
  }
  // This hard-codes values that do not affect the length calculation. Ideally we would have a narrowed helper for this use-case.
  const {length} = validateEndpoints(
    endpoints,
    basicPolymerInfo.residues.length,
    1,
    orientation,
    {restrictionEnzymeId: null, cutsiteIndices: null},
    {restrictionEnzymeId: null, cutsiteIndices: null},
    canSpecifyEndpoints,
    basicPolymerInfo.circular,
    null
  );
  if (length == null) {
    if (
      isBANumber(endpoints.start) &&
      endpoints.start.value >= 0 &&
      endpoints.start.value < basicPolymerInfo.residues.length
    ) {
      return {
        length: 0,
        visualStart: endpoints.start.value,
        visualEnd: null,
      };
    } else {
      return {
        length: 0,
        visualStart: 0,
        visualEnd: null,
      };
    }
  } else {
    return {
      length,
      visualStart: (endpoints.start as BANumberValue).value,
      visualEnd: (endpoints.end as BANumberValue).value,
    };
  }
}
